import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { featuredProducts } from '../../mocks/products';
import { productBundles } from '../../mocks/bundles';
import ProductQuickView from './components/ProductQuickView';
import SocialShare from '../../components/feature/SocialShare';
import NewsletterSignup from '../../components/feature/NewsletterSignup';
import UrgencyTimer from '../../components/feature/UrgencyTimer';
import TrustBadges from '../../components/feature/TrustBadges';
import ExitIntentPopup from '../../components/feature/ExitIntentPopup';
import { addToCart } from '../../utils/cart';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/base/Toast';

const HomePage = () => {
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showAdBanner, setShowAdBanner] = useState(true);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const navigate = useNavigate();
  const { toasts, removeToast, success, error } = useToast();

  const handleAddToCart = (product: typeof featuredProducts[0]) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1
    });
    success(`${product.name} added to cart!`);
  };

  const handleBundleAddToCart = (bundle: typeof productBundles[0]) => {
    // Add all products in the bundle to cart
    bundle.products.forEach(product => {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1
      });
    });
    success(`${bundle.name} added to cart! (${bundle.products.length} items)`);
  };

  // Exit Intent Detection
  useEffect(() => {
    let hasShownPopup = false;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShownPopup) {
        hasShownPopup = true;
        setShowExitPopup(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Urgency Timer Banner */}
      <UrgencyTimer 
        title="⚡ Flash Sale Ends In:"
        description="Up to 50% OFF on selected items!"
      />

      {/* Hot Deals Banner - NEW */}
      <section className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full animate-pulse">
                <i className="ri-fire-fill text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-xl">🔥 Hot Deals Under $50!</h3>
                <p className="text-white/90 text-sm">Limited time offers on trending items</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/products', { state: { category: 'Trending Under $50' } })}
              className="px-6 py-3 bg-white text-red-500 rounded-lg font-bold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
            >
              Shop Hot Deals <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Floating Advertisement Banner */}
      {showAdBanner && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-slide-up">
          <div className="bg-gradient-to-br from-primary via-purple-600 to-accent-green rounded-2xl shadow-2xl overflow-hidden">
            <button
              onClick={() => setShowAdBanner(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line"></i>
            </button>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-white text-xs font-bold uppercase tracking-wider">Hot Deal</span>
              </div>
              
              <h3 className="text-white font-bold text-xl mb-2">
                🔥 Limited Time Offers!
              </h3>
              
              <div className="space-y-3 mb-4">
                <div 
                  onClick={() => {
                    navigate('/products');
                    setShowAdBanner(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-macbook-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">MacBook Pro 16"</p>
                      <p className="text-white/80 text-xs">Save $500 Today!</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  onClick={() => {
                    navigate('/products');
                    setShowAdBanner(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-plane-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">Maldives Luxury</p>
                      <p className="text-white/80 text-xs">Earn $1,500 Commission!</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  navigate('/products');
                  setShowAdBanner(false);
                }}
                className="w-full py-3 bg-white text-primary rounded-lg font-bold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Shop Now <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/3d004c220dabb9a4066d0a6d850d05d9.jpg)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>

        <div className="relative z-10 px-6 lg:px-12 py-20 w-full">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <span className="text-sm font-medium">
                🎉 Earn 30% Commission on Every Sale
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Premium Digital Products
              <br />
              <span className="text-primary">Automated Dropshipping</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Zero inventory. Zero shipping. Products ship directly from suppliers to
              customers. You earn commission on every sale.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => navigate('/products')}
                className="px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                Browse Products
              </button>
              <button
                onClick={() => navigate('/services')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                Explore Services
              </button>
            </div>

            {/* Social Share */}
            <div className="flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <SocialShare 
                  title="Digital Business - Automated Dropshipping Platform"
                  description="Earn 30% commission with automated dropshipping. Zero inventory, zero shipping!"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-3xl font-bold text-primary mb-2">500+</div>
                <div className="text-sm text-gray-200">Products</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-3xl font-bold text-primary mb-2">10K+</div>
                <div className="text-sm text-gray-200">Happy Customers</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-3xl font-bold text-primary mb-2">30%</div>
                <div className="text-sm text-gray-200">Commission</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-3xl font-bold text-primary mb-2">24/7</div>
                <div className="text-sm text-gray-200">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges />

      {/* Special Promotions Section - NEW */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              🎁 Special Promotions
            </h2>
            <p className="text-lg text-gray-600">
              Limited time offers you don't want to miss!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Promotion 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-500 rounded-full mb-4 mx-auto">
                <i className="ri-percent-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Buy 3, Save 15%</h3>
              <p className="text-gray-600 text-center mb-4">
                Purchase any 3 items under $50 and get 15% off your entire order!
              </p>
              <div className="text-center">
                <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                  Code: SAVE15
                </span>
              </div>
            </div>

            {/* Promotion 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary to-accent-green rounded-full mb-4 mx-auto">
                <i className="ri-gift-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Free Shipping</h3>
              <p className="text-gray-600 text-center mb-4">
                Get free shipping on all orders over $75. No code needed!
              </p>
              <div className="text-center">
                <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
                  Auto Applied
                </span>
              </div>
            </div>

            {/* Promotion 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4 mx-auto">
                <i className="ri-vip-crown-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">First Order 20% Off</h3>
              <p className="text-gray-600 text-center mb-4">
                New customers get 20% off their first purchase. Welcome aboard!
              </p>
              <div className="text-center">
                <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                  Code: WELCOME20
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Bundles Section - NEW */}
      <section className="py-24 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              💰 Bundle & Save
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get more for less with our curated product bundles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productBundles.map((bundle) => (
              <div
                key={bundle.id}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={bundle.image}
                    alt={bundle.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    Save ${bundle.savings.toFixed(2)}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{bundle.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{bundle.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Includes:</p>
                    <ul className="space-y-1">
                      {bundle.products.map((product, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                          <i className="ri-checkbox-circle-fill text-accent-green"></i>
                          {product.name}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-sm text-gray-500 line-through">
                        ${bundle.originalPrice.toFixed(2)}
                      </div>
                      <div className="text-2xl font-bold text-primary">
                        ${bundle.bundlePrice.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">You Save</div>
                      <div className="text-lg font-bold text-red-500">
                        ${bundle.savings.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleBundleAddToCart(bundle)}
                    className="w-full bg-gradient-to-r from-primary to-accent-green text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-line mr-2"></i>
                    Add Bundle to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All You Need in One Location */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-accent-cyan/5 via-white to-accent-green/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              All You Need in One Location
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From cutting-edge electronics to luxury travel experiences, premium fashion to gourmet delights – everything you desire, all in one convenient marketplace
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
            {[
              { icon: 'ri-smartphone-line', title: 'Electronics', count: '500+', color: 'from-blue-500 to-cyan-500' },
              { icon: 'ri-shirt-line', title: 'Fashion', count: '1000+', color: 'from-pink-500 to-rose-500' },
              { icon: 'ri-plane-line', title: 'Travel', count: '200+', color: 'from-orange-500 to-amber-500' },
              { icon: 'ri-restaurant-line', title: 'Gourmet', count: '300+', color: 'from-green-500 to-emerald-500' },
              { icon: 'ri-home-gear-line', title: 'Home & Living', count: '800+', color: 'from-purple-500 to-violet-500' },
              { icon: 'ri-heart-pulse-line', title: 'Health & Fitness', count: '400+', color: 'from-red-500 to-pink-500' },
            ].map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className={`w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gradient-to-br ${category.color} rounded-full group-hover:scale-110 transition-transform`}>
                  <i className={`${category.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{category.title}</h3>
                <p className="text-sm text-gray-500">{category.count} items</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-global-line', title: 'Worldwide Shipping', desc: 'Fast delivery to your doorstep' },
              { icon: 'ri-shield-check-line', title: 'Secure Payments', desc: 'SSL encrypted transactions' },
              { icon: 'ri-customer-service-2-line', title: '24/7 Support', desc: 'Always here to help you' },
              { icon: 'ri-money-dollar-circle-line', title: 'Best Prices', desc: 'Competitive pricing guaranteed' },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary/10 text-primary rounded-lg mb-4">
                  <i className={`${feature.icon} text-2xl`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Featured Products
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our curated selection of premium digital products and services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {featuredProducts.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="relative w-full h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      <button 
                        onClick={() => setSelectedProduct(product)}
                        className="w-full px-4 py-2 bg-white/90 backdrop-blur-sm text-gray-900 rounded-lg font-medium hover:bg-white transition-colors whitespace-nowrap"
                      >
                        Quick View
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-accent-cyan/10 text-accent-cyan text-xs font-semibold rounded-full mb-3">
                    {product.category}
                  </span>
                  <h3 className="font-semibold text-xl text-gray-900 mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary">
                      ${product.price}
                    </span>
                    <button 
                      onClick={() => handleAddToCart(product)}
                      className="w-10 h-10 flex items-center justify-center bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors cursor-pointer"
                    >
                      <i className="ri-shopping-cart-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-block px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional digital services to help your business grow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-code-s-slash-line', title: 'Web Development', desc: 'Custom websites and web applications' },
              { icon: 'ri-palette-line', title: 'Design Services', desc: 'Brand identity and graphic design' },
              { icon: 'ri-megaphone-line', title: 'Digital Marketing', desc: 'SEO, social media, and content marketing' },
              { icon: 'ri-shopping-bag-line', title: 'E-commerce Solutions', desc: 'Complete online store setup' },
              { icon: 'ri-line-chart-line', title: 'Business Consulting', desc: 'Strategy and growth consulting' },
              { icon: 'ri-customer-service-line', title: '24/7 Support', desc: 'Dedicated customer support team' },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary/10 text-primary rounded-lg mb-4">
                  <i className={`${service.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Digital Business?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We provide premium digital products and services at competitive prices with exceptional customer service and secure, fast checkout.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-shield-check-line', title: 'Secure Checkout', desc: 'SSL encrypted payment processing' },
                  { icon: 'ri-flashlight-line', title: 'Fast Delivery', desc: 'Instant access to digital products' },
                  { icon: 'ri-price-tag-3-line', title: 'Great Prices', desc: 'Competitive pricing on all items' },
                  { icon: 'ri-customer-service-2-line', title: 'Expert Support', desc: '24/7 customer assistance' },
                ].map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent-green/10 text-accent-green rounded-lg flex-shrink-0">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-full h-96 lg:h-full min-h-96">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20professional%20business%20team%20working%20together%20on%20digital%20projects%20in%20bright%20minimalist%20office%20with%20laptops%20tablets%20and%20collaborative%20workspace%20showing%20success%20and%20innovation%20with%20clean%20white%20walls%20and%20natural%20lighting&width=800&height=900&seq=why1&orientation=portrait"
                alt="Why Choose Us"
                className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-r from-primary to-accent-green">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of satisfied customers and discover premium products at great prices with high commission earnings
          </p>
          <Link
            to="/products"
            className="inline-block px-10 py-4 bg-white text-primary rounded-lg font-semibold hover:shadow-2xl transition-all text-lg cursor-pointer whitespace-nowrap"
          >
            Shop Now
          </Link>
        </div>
      </section>

      {/* Newsletter Section - Add before Footer */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      <Footer />

      {/* Quick View Modal */}
      {selectedProduct && (
        <ProductQuickView
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <ExitIntentPopup onClose={() => setShowExitPopup(false)} />
      )}

      {/* Toast Notifications */}
      <div className="fixed top-0 right-0 z-[9999] pointer-events-none">
        <div className="pointer-events-auto">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
